import { Component } from 'react';

import { Navigate, Outlet, useLocation } from '@suntex.core/nav';
import { AuthError, AuthState } from '@suntex.fim/auth';
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react';

export interface RequireAuthProps {
  /** Path to redirect to when not authenticated. Defaults to '/' */
  loginPath?: string;
  /** Path to redirect to on authorization errors. Defaults to '/auth-error' */
  errorPath?: string;
  /** Additional authorization check run once authenticated. */
  requirement?: (auth: AuthState) => boolean;
  /** Path to redirect to when `requirement` fails. Defaults to '/' */
  forbiddenPath?: string;
}

interface RequireAuthState {
  authError: AuthError | null;
}

/**
 * Route guard component that protects routes requiring authentication.
 * Also acts as an error boundary for AuthError, redirecting on auth failures.
 *
 * @example
 * ```tsx
 * <Route element={<RequireAuth />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 *   <Route path="/profile" element={<Profile />} />
 * </Route>
 * ```
 */
export class RequireAuth extends Component<RequireAuthProps, RequireAuthState> {
  state: RequireAuthState = { authError: null };

  static getDerivedStateFromError(error: Error): RequireAuthState | null {
    if (error instanceof AuthError) {
      return { authError: error };
    }
    // Re-throw non-auth errors to propagate to other error boundaries
    return null;
  }

  componentDidCatch(error: Error): void {
    // Only handle AuthError, re-throw others
    if (!(error instanceof AuthError)) {
      throw error;
    }
  }

  render() {
    const { authError } = this.state;

    if (authError) {
      // Redirect to auth error page with error details
      return (
        <AuthErrorRedirect error={authError} errorPath={this.props.errorPath} />
      );
    }

    return (
      <RequireAuthContent
        loginPath={this.props.loginPath}
        requirement={this.props.requirement}
        forbiddenPath={this.props.forbiddenPath}
      />
    );
  }
}

/**
 * Component to handle redirect to auth error page
 */
// eslint-disable-next-line react-refresh/only-export-components
function AuthErrorRedirect({
  error,
  errorPath = '/auth-error',
}: {
  error: AuthError;
  errorPath?: string;
}) {
  const params = new URLSearchParams();
  params.set('message', error.message);
  if (error.code) {
    params.set('code', error.code);
  }
  return <Navigate to={`${errorPath}?${params.toString()}`} replace />;
}

/**
 * Inner component that uses hooks (can't use hooks in class components)
 */
// eslint-disable-next-line react-refresh/only-export-components
function RequireAuthContent({
  loginPath = '/',
  requirement,
  forbiddenPath = '/',
}: RequireAuthProps) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.isLoading) {
    return null;
  }

  if (!isAuthenticated(auth)) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (requirement && !requirement(auth)) {
    return <Navigate to={forbiddenPath} replace />;
  }

  return <Outlet />;
}
